import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { delayedRetry } from '@shared/operators/delayed-retry.operator';
import { Observable, interval, of } from 'rxjs';
import { shareReplay, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ScrollService } from './scroll.service';
import { API } from '@core/constants';

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	//Code block - Caching - RamA
	public cache: Map<string, Observable<any>> = new Map();

	private startCacheClearingInterval(): void {
		interval(8 * 60 * 1000) // Emit a value every 5 minutes
			.subscribe(() => {
				this.clearCache();
			});
	}

	private clearCache(): void {
		this.cache.clear();
		localStorage.removeItem('pageMap');
		this.scroll.saveAdListPageNo(undefined);
		this.scroll.saveHomeListPageNo(undefined);
	}

	getCacheKey(url: string, body: any, page?): string {
		const serializedBody = JSON.stringify(body);
		return `${url}?${page}_${serializedBody}`;
	}

	storeCacheData() {
		const cacheDataMap = new Map<string, string>();
		const timestamp = new Date().getTime(); // Get current timestamp
		this.cache.forEach((observable: Observable<any>, key: string) => {
			observable.pipe(take(1)).subscribe((value: any) => {
				cacheDataMap.set(key, JSON.stringify(value));
			});
		});
		const dataWithTimestamp = {
			data: JSON.stringify(Array.from(cacheDataMap.entries())),
			time: timestamp,
		};
		localStorage.setItem('pageMap', JSON.stringify(dataWithTimestamp));
	}

	retrieveCacheData() {
		const cachedData = localStorage.getItem('pageMap');
		if (cachedData) {
			const parsedData = JSON.parse(cachedData);
			const { data: data, time: timeStamp } = parsedData;
			const cacheDataMap = new Map<string, string>(JSON.parse(data));

			const currentTime = new Date().getTime();
			const timeDifference = currentTime - timeStamp;
			const timeThreshold = 8 * 60; // 8 minutes in milliseconds

			if (timeDifference <= timeThreshold) {
				cacheDataMap.forEach((value: string, key: string) => {
					const cachedValue = JSON.parse(value);
					this.cache.set(key, of(cachedValue).pipe(shareReplay()));
				});
			} else {
				localStorage.removeItem('pageMap');
			}
		}
	}
	//Code block - Caching - RamA

	constructor(private http: HttpClient, private scroll: ScrollService) {
		// Start the cache clearing interval
		this.retrieveCacheData();
		this.startCacheClearingInterval(); //Caching
	}

	private endpoint = environment['api'];
	private firebase_api_key = 'AIzaSyDrlSGfsadNJIo8aH2SDkCibVUPCPWHZtk';

	createDeepLink(body: Object) {
		return this.http.post('https://firebasedynamiclinks.googleapis.com/v1/shortLinks?key=' + this.firebase_api_key, body).pipe(delayedRetry());
	}

	getTranslations(body: FormData) {
		return this.http.post(this.endpoint + 'getTranslations', body).pipe(delayedRetry());
	}

	validateToken(body: FormData) {
		return this.http.post(this.endpoint + 'validateToken', body).pipe(delayedRetry());
	}

	validateAuthToken(token: string) {
		const headers = new HttpHeaders({
			Authorization: `Bearer ${token}`,
		});

		return this.http.post(API.auth.validateToken, {}, { headers });
	}

	profile(body: FormData) {
		return this.http.post(this.endpoint + 'profile', body).pipe(delayedRetry());
	}

	updateProfile(body: FormData) {
		return this.http.post(this.endpoint + 'updateProfile', body).pipe(delayedRetry());
	}

	resetPassword(body: FormData) {
		return this.http.post(this.endpoint + 'resetPassword', body).pipe(delayedRetry());
	}

	ads(body: FormData, url: string, page?) {
		//Code Block - Caching - RamA
		const cacheKey = this.getCacheKey(url, body, page);
		const cachedResponse = this.cache.get(cacheKey);

		if (cachedResponse) {
			return cachedResponse;
		} else {
			let resp = this.http
				.post(this.endpoint + 'ads', body)
				.pipe(delayedRetry())
				.pipe(shareReplay(1));
			this.cache.set(cacheKey, resp);
			return resp;
		}
		//Code Block - Caching - RamA

		// return this.http.post(this.endpoint + 'ads', body).pipe(delayedRetry());
	}

	ad(body: FormData) {
		return this.http.post(this.endpoint + 'ad', body).pipe(delayedRetry());
	}

	userAds(body: FormData) {
		return this.http.post(this.endpoint + 'userAds', body).pipe(delayedRetry());
	}

	userWatchlist(body: FormData) {
		return this.http.post(this.endpoint + 'userWatchlist', body).pipe(delayedRetry());
	}

	watch(body: FormData | any) {
		return this.http.post(this.endpoint + 'watch', body).pipe(delayedRetry());
	}

	categories(body: FormData) {
		return this.http.post(this.endpoint + 'categories', body).pipe(delayedRetry());
	}

	models(body: FormData) {
		return this.http.post(this.endpoint + 'models', body).pipe(delayedRetry());
	}

	locations(body: FormData) {
		return this.http.post(this.endpoint + 'locations', body).pipe(delayedRetry());
	}

	motorData(body: FormData) {
		return this.http.post(this.endpoint + 'motorData', body).pipe(delayedRetry());
	}

	call(body: FormData | any) {
		return this.http.post(this.endpoint + 'call', body).pipe(delayedRetry());
	}

	sell(body: FormData) {
		return this.http.post(this.endpoint + 'sell', body).pipe(delayedRetry());
	}

	deleteAd(body: FormData) {
		return this.http.post(this.endpoint + 'deleteAd', body).pipe(delayedRetry());
	}

	placeAd(body: FormData) {
		return this.http.post(this.endpoint + 'placeAd', body).pipe(delayedRetry());
	}

	editAd(body: FormData) {
		return this.http.post(this.endpoint + 'editAd', body).pipe(delayedRetry());
	}

	uploadImageRaw(body: FormData) {
		return this.http
			.post(this.endpoint + 'uploadImageRaw', body, {
				reportProgress: true,
				observe: 'events',
			})
			.pipe(delayedRetry());
	}

	deleteImage(body: FormData) {
		let path = body.get('userID') ? 'deleteImage' : 'imageDetach';
		return this.http.post(this.endpoint + path, body).pipe(delayedRetry());
	}

	payVoucher(body: FormData) {
		return this.http.post(this.endpoint + 'payVoucher', body).pipe(delayedRetry());
	}

	validateOTP(body: FormData) {
		return this.http.post(this.endpoint + 'validateOTP', body).pipe(delayedRetry());
	}

	sendOTP(body: FormData) {
		return this.http.post(this.endpoint + 'sendOTP', body).pipe(delayedRetry());
	}

	requestCampaign(body: FormData) {
		return this.http.post(this.endpoint + 'requestCampaign', body).pipe(delayedRetry());
	}

	PhoneRegistered(body: FormData) {
		return this.http.post(this.endpoint + 'checkPhoneRegistered', body).pipe(delayedRetry());
	}

	verifyPhone(body: FormData) {
		return this.http.post(this.endpoint + 'verifyPhone', body).pipe(delayedRetry());
	}

	electronicFilterData(body: FormData) {
		return this.http.post(this.endpoint + 'electronicFilterData', body).pipe(delayedRetry());
	}

	priceFilterData(body: FormData) {
		return this.http.post(this.endpoint + 'priceFilterData', body).pipe(delayedRetry());
	}

	electronicModels(body: FormData) {
		return this.http.post(this.endpoint + 'electronicModels', body).pipe(delayedRetry());
	}

	getVersionHistory(body) {
		let params = new HttpParams().set('platform', body['platform']).set('lang', body['lang']);

		return this.http.get(this.endpoint + 'getVersionHistory', { params: params }).pipe(delayedRetry());
	}

	socialMediaAnalytics(body) {
		return this.http.post(this.endpoint + 'shareAnalytics', body).pipe(delayedRetry());
	}

	createSession(body) {
		return this.http.post(this.endpoint + 'createSession', body).pipe(delayedRetry());
	}

	verifyPayment(body) {
		return this.http.post(this.endpoint + 'verifyPayment', body).pipe(delayedRetry());
	}

	upgradeAd(body) {
		return this.http.post(this.endpoint + 'upgradeAd', body).pipe(delayedRetry());
	}

	verifyTempToken(body) {
		return this.http.post(this.endpoint + 'verifyTempToken', body).pipe(delayedRetry());
	}

	paymentMethods(body) {
		return this.http.post(this.endpoint + 'paymentMethods', body).pipe(delayedRetry());
	}

	uploadImage(body: FormData) {
		return this.http
			.post(this.endpoint + 'uploadImage', body, {
				reportProgress: true,
				observe: 'events',
			})
			.pipe(delayedRetry());
	}

	getProviders(body: FormData) {
		return this.http.post(this.endpoint + 'providers', body).pipe(delayedRetry());
	}

	getMissedCallsList(body: FormData) {
		return this.http.post(this.endpoint + 'ad/calls', body).pipe(delayedRetry());
	}

	getProductValues(body) {
		let params = new HttpParams().set('lang', body['lang']);
		return this.http.get(this.endpoint + 'getProductValues', { params: params }).pipe(delayedRetry());
	}

	//THESE API'S ARE NOT IN USE
	deleteAccount(body: FormData) {
		return this.http.post(this.endpoint + 'deleteAccount', body).pipe(delayedRetry());
	}

	updatePassword(body: FormData) {
		return this.http.post(this.endpoint + 'updatePassword', body).pipe(delayedRetry());
	}

	sendResetPasswordOTP(body: FormData) {
		return this.http.post(this.endpoint + 'sendResetPasswordOTP', body).pipe(delayedRetry());
	}

	signIn(body: FormData) {
		return this.http.post(this.endpoint + 'signIn', body).pipe(delayedRetry());
	}

	signUp(body: FormData) {
		return this.http.post(this.endpoint + 'signUp', body).pipe(delayedRetry());
	}

	tempToken(body) {
		return this.http.post(this.endpoint + 'tempToken', body).pipe(delayedRetry());
	}

	updatePhone(body: FormData) {
		return this.http.post(this.endpoint + 'updatePhone', body).pipe(delayedRetry());
	}

	//THESE API'S ARE NOT IN USE AS DEALER'S SECTION IS NOW REMOVED
	dealerUpdateProfile(body: FormData) {
		return this.http.post(this.endpoint + 'createDealerRequest', body).pipe(delayedRetry());
	}

	upgradeAdFromCredit(body: FormData) {
		return this.http.post(this.endpoint + 'upgradeAdFromCredit', body).pipe(delayedRetry());
	}

	dealerProfile(body: FormData) {
		return this.http.post(this.endpoint + 'dealerProfile', body).pipe(delayedRetry());
	}

	dealers(body) {
		return this.http.post(this.endpoint + 'dealers', body).pipe(delayedRetry());
	}

	dealerAds(body) {
		return this.http.post(this.endpoint + 'dealerAds', body).pipe(delayedRetry());
	}

	//THESE API'S ARE NOT IN USE AS COMMENT'S SECTION IS NOW REMOVED
	addComment(body: FormData) {
		return this.http.post(this.endpoint + 'addComment', body).pipe(delayedRetry());
	}

	comments(body: FormData) {
		return this.http.post(this.endpoint + 'comments', body).pipe(delayedRetry());
	}

	commentReplies(body: FormData) {
		return this.http.post(this.endpoint + 'commentReplies', body).pipe(delayedRetry());
	}

	hideComment(body: FormData) {
		return this.http.post(this.endpoint + 'hideComment', body).pipe(delayedRetry());
	}

	saveAlerts(body: any) {
		return this.http.post(this.endpoint + 'user/preferences', body);
	}

	getAlerts() {
		return this.http.get(this.endpoint + 'user/preferences');
	}
}
